import type { ReactNode } from 'react';

export type PillTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/**
 * One vocabulary for lifecycle states across the product.
 * open → blue · paid/confirmed/active/collected/approved → green
 * pending/draft/sent/issued → amber · void/cancelled/failed → red · closed/inactive → grey
 */
const TONES: Record<string, PillTone> = {
  open: 'info',
  live: 'info',
  in_progress: 'info',
  paid: 'success',
  confirmed: 'success',
  active: 'success',
  collected: 'success',
  approved: 'success',
  received: 'success',
  issued: 'warning',
  done: 'success',
  yes: 'success',
  pending: 'warning',
  draft: 'warning',
  sent: 'warning',
  unpaid: 'warning',
  partial: 'warning',
  waiting: 'warning',
  void: 'danger',
  voided: 'danger',
  cancelled: 'danger',
  canceled: 'danger',
  failed: 'danger',
  refunded: 'danger',
  rejected: 'danger',
  forced: 'danger',
  closed: 'neutral',
  inactive: 'neutral',
  archived: 'neutral',
  no: 'neutral',
  unknown: 'neutral',
};

export function toneForStatus(status: string): PillTone {
  return TONES[status.toLowerCase().replace(/[\s-]+/g, '_')] ?? 'neutral';
}

interface StatusPillProps {
  status: string;
  /** Override the automatic tone. */
  tone?: PillTone;
  /** Override the label (defaults to the status, sentence-cased). */
  children?: ReactNode;
  dot?: boolean;
  className?: string;
}

function sentence(s: string): string {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function StatusPill({ status, tone, children, dot = true, className }: StatusPillProps) {
  const t = tone ?? toneForStatus(status);
  const cls = ['ui-pill', `ui-pill--${t}`, dot ? '' : 'ui-pill--nodot', className]
    .filter(Boolean)
    .join(' ');
  return <span className={cls}>{children ?? sentence(status)}</span>;
}
